import { useEffect, useRef, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, BarChart3, Download, Users, Video, Clock, LogIn, LogOut, ClipboardList, Star, X, CheckSquare, FileText, Search, RefreshCw, Loader2, ChevronDown, PlayCircle, ExternalLink, MapPin, Smartphone, AlertTriangle } from 'lucide-react';
import { api, formatDate, downloadCsv } from '../../lib/api';
import { downloadRosterPdf } from '../../lib/roster-pdf';
import EngineProgressTab from '../../components/admin/EngineProgress';
import {
  useScrollShadow, STICKY_CELL, STICKY_HEAD_BG, STICKY_CELL_BG, STICKY_SHADOW,
} from '../../lib/sticky-column';

const TABS = [
  { key: 'roster', label: 'Student Roster', icon: Users },
  { key: 'attendance', label: 'Zoom Attendance', icon: Video },
  { key: 'evaluations', label: 'Evaluations', icon: ClipboardList },
  { key: 'recording', label: 'Recording', icon: PlayCircle },
  { key: 'transcript', label: 'Transcript', icon: FileText },
  { key: 'checkins', label: 'Door Check-ins', icon: Smartphone },
  { key: 'progress', label: 'Learner progress', icon: BarChart3 },
];

// Learnable's own approval/provider numbers per credentialing agency. Hard-coded
// (they belong to Learnable, not the participant) and stamped on the roster CSV
// preamble. Canonical agency order also drives the participant credential columns.
const PROVIDER_NUMBERS = [
  { agency: 'NBCC', number: 'SP-5258' },
  { agency: 'LPCA', number: 'M-0126-140' },
  { agency: 'GACA', number: '26-212-09' },
];
const PROVIDER_LINE = PROVIDER_NUMBERS.map((p) => `${p.agency} ${p.number}`).join('; ');

// Format a session date as "17-Jun-26". Parses a leading YYYY-MM-DD in local
// terms so a date-only / UTC value doesn't roll back a day in western timezones.
const formatStampDate = (d) => {
  if (!d) return '';
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const dt = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(d);
  if (isNaN(dt.getTime())) return '';
  const mon = dt.toLocaleString('en-US', { month: 'short' });
  return `${dt.getDate()}-${mon}-${String(dt.getFullYear()).slice(-2)}`;
};

export default function AdminCourseReports() {
  const { id } = useParams();
  const location = useLocation();
  const backTo = location.state?.from === 'reports' ? '/admin/reports' : '/admin/courses';
  const backLabel = location.state?.from === 'reports' ? 'Back to reports' : 'Back to courses';
  const [course, setCourse] = useState(null);
  // ?tab=progress returns here from a learner's detail page.
  const [tab, setTab] = useState(() => new URLSearchParams(location.search).get('tab') || 'roster');
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [noShows, setNoShows] = useState([]);
  const [checkinWindows, setCheckinWindows] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [flowProgress, setFlowProgress] = useState([]);
  const [evalResponses, setEvalResponses] = useState([]);
  const [rosterOnlyCompleted, setRosterOnlyCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sendingEvalReminders, setSendingEvalReminders] = useState(false);
  const [evalReminderResult, setEvalReminderResult] = useState(null);
  const [verifyingId, setVerifyingId] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [expandedEvals, setExpandedEvals] = useState(() => new Set());
  const [expandedBars, setExpandedBars] = useState(() => new Set());
  const [rosterTableRef, rosterShadowed] = useScrollShadow();
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [recordingLoading, setRecordingLoading] = useState(false);
  const [recordingError, setRecordingError] = useState(null);

  const load = (silent) => {
    if (!silent) setLoading(true);
    Promise.all([
      api(`/api/admin/courses`).then((d) => (d.courses || []).find((c) => c.id === id)),
      api(`/api/admin/courses/${id}/students`),
      api(`/api/admin/courses/${id}/attendance`),
      api(`/api/admin/courses/${id}/evaluation-responses`).catch(() => ({ responses: [] })),
      api(`/api/admin/courses/${id}/checkin-windows`).catch(() => ({ checkin_windows: [] })),
      api(`/api/admin/courses/${id}/checkins`).catch(() => ({ checkins: [] })),
    ])
      .then(([c, s, a, e, w, ci]) => {
        setCourse(c || null);
        setStudents(s.students || []);
        setAttendance(a.attendance || []);
        setNoShows(a.no_shows || []);
        setFlowProgress(a.flow_progress || []);
        setEvalResponses(e.responses || []);
        setCheckinWindows(w.checkin_windows || []);
        setCheckins(ci.checkins || []);
      })
      .finally(() => { if (!silent) setLoading(false); });
  };

  useEffect(() => { load(); }, [id]);

  if (loading) return <div className="p-8 text-slate-500">Loading...</div>;
  if (!course) return <div className="p-8 text-slate-500">Course not found.</div>;

  const isHybrid = course.course_type === 'hybrid';
  // Hybrid events run the whole live pipeline, so every live-gated column and
  // control applies to them too.
  const isLive = course.course_type === 'live' || isHybrid;
  const isEngine = course.course_type === 'async' && course.content_format === 'engine';

  // "No-show" only means something once the session has actually begun. Before
  // the start time an enrollee who hasn't opened anything is simply early, not
  // absent, so the roster badges them "not started" until then. A live course
  // with no scheduled date never reports a no-show either — there is nothing
  // yet to have missed.
  const sessionStarted = Boolean(
    course.live_date_utc && new Date(course.live_date_utc).getTime() <= Date.now()
  );

  const verifiedUserIds = new Set(flowProgress.filter((p) => p.attendance_verified_at).map((p) => p.user_id));

  const checkIn = async (userId, name, windowId = null) => {
    const w = windowId && checkinWindows.find((x) => x.id === windowId);
    const what = w ? `for "${w.label}"` : 'as attending in person';
    if (!confirm(`Check ${name} in ${what}?`)) return;
    setVerifyingId(userId);
    try {
      const res = await api(`/api/admin/courses/${id}/check-in`, {
        method: 'POST', body: { user_id: userId, ...(windowId ? { window_id: windowId } : {}) },
      });
      if (res.required_total > 1 && !res.verified) {
        alert(`Checked in for ${res.window_label}. ${res.covered} of ${res.required_total} windows — they still need the rest before attendance verifies.`);
      }
      load(true);
    } catch (err) {
      // Outside every window, the server won't guess which segment to credit.
      if (err.body?.error === 'window_required') {
        const opts = (err.body.windows || []).map((x, i) => `${i + 1}. ${x.label} (${x.state})`).join('\n');
        const pick = prompt(`No check-in window is open right now.\nWhich one should ${name} be credited for?\n\n${opts}\n\nEnter a number:`);
        const idx = parseInt(pick, 10);
        if (idx >= 1 && idx <= (err.body.windows || []).length) {
          setVerifyingId(null);
          return checkIn(userId, name, err.body.windows[idx - 1].id);
        }
        return;
      }
      alert('Failed to check that person in.');
    } finally {
      setVerifyingId(null);
    }
  };

  const setAttendanceMode = async (enrollmentId, mode, name) => {
    try {
      await api(`/api/admin/enrollments/${enrollmentId}/attendance-mode`, {
        method: 'PATCH', body: { attendance_mode: mode },
      });
      load(true);
    } catch (err) {
      // A full room refuses the move once, then allows it deliberately —
      // seating an extra person is a real decision, just not an accidental one.
      if (err.body?.error === 'in_person_full') {
        const { taken, capacity } = err.body;
        if (!confirm(
          `The room is full — ${taken} of ${capacity} in-person seats are taken.\n\n` +
          `Seat ${name} in person anyway? This puts the event over its capacity.`
        )) { load(true); return; }
        try {
          await api(`/api/admin/enrollments/${enrollmentId}/attendance-mode`, {
            method: 'PATCH', body: { attendance_mode: mode, override: true },
          });
          load(true);
        } catch {
          alert('Could not change the attendance mode.');
        }
        return;
      }
      alert('Could not change the attendance mode.');
    }
  };

  const verifyAttendance = async (userId, name) => {
    if (!confirm(`Manually verify attendance for ${name}? This overrides the Zoom attendance check and unlocks their Evaluation step.`)) return;
    setVerifyingId(userId);
    try {
      await api(`/api/admin/courses/${id}/manual-verify-attendance`, { method: 'POST', body: { user_id: userId } });
      load(true);
    } catch {
      alert('Failed to verify attendance.');
    } finally {
      setVerifyingId(null);
    }
  };

  // Fetch the (time-limited) Zoom cloud recording URL on demand. Admin-only —
  // recordings are no longer surfaced to students.
  const loadRecording = async () => {
    setRecordingLoading(true);
    setRecordingError(null);
    try {
      const { url } = await api(`/api/admin/courses/${id}/recording`);
      setRecordingUrl(url || null);
      if (!url) setRecordingError('Recording is not available yet.');
    } catch (err) {
      setRecordingUrl(null);
      setRecordingError(err.body?.detail || 'Recording is not available yet.');
    } finally {
      setRecordingLoading(false);
    }
  };

  const toggleEval = (eid) =>
    setExpandedEvals((prev) => {
      const next = new Set(prev);
      if (next.has(eid)) next.delete(eid); else next.add(eid);
      return next;
    });

  const toggleBar = (key) =>
    setExpandedBars((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  // Shared roster data for both exports. Participant credential columns are
  // dynamic — driven by the agencies the credential (multi_select_text) question
  // captures — ordered by Learnable's canonical list (NBCC, LPCA, GACA) with any
  // custom agencies appended.
  const buildRosterRecords = () => {
    const canonical = PROVIDER_NUMBERS.map((p) => p.agency);
    const agencies = credentialOptions.length
      ? [
          ...canonical.filter((a) => credentialOptions.includes(a)),
          ...credentialOptions.filter((a) => !canonical.includes(a)),
        ]
      : canonical;
    const records = rosterStudents.map((s) => {
      const cred = credForEnrollment(s.enrollment_id);
      return {
        name: s.full_name,
        email: s.email,
        status: s.status,
        progress: `${s.progress_percent}%`,
        score: s.score ?? '',
        enrolled: s.enrolled_at ? new Date(s.enrolled_at).toLocaleDateString() : '',
        completed: s.completed_at ? new Date(s.completed_at).toLocaleDateString() : '',
        certificate: s.certificate_number || '',
        creds: Object.fromEntries(agencies.map((a) => [a, cred.values?.[a] || ''])),
      };
    });
    return { agencies, records };
  };

  const exportRoster = () => {
    const { agencies, records } = buildRosterRecords();
    const rows = records.map((r) => {
      const row = {
        name: r.name, email: r.email, status: r.status, progress: r.progress,
        score: r.score, enrolled: r.enrolled, completed: r.completed, certificate: r.certificate,
        '': '', // blank separator column before the credential columns
      };
      for (const a of agencies) row[a] = r.creds[a] || '';
      return row;
    });
    // Preamble rows above the header: session date, course title, and Learnable's
    // hard-coded provider numbers.
    const preamble = [
      [formatStampDate(course.live_date || course.live_date_utc)],
      [course.title],
      [PROVIDER_LINE],
    ];
    downloadCsv(
      rows,
      `${course.slug}-students-${new Date().toISOString().slice(0, 10)}.csv`,
      preamble,
    );
  };

  const exportRosterPdf = async () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    try {
      const { agencies, records } = buildRosterRecords();
      const stamp = formatStampDate(course.live_date || course.live_date_utc);
      await downloadRosterPdf({
        filename: `${course.slug}-roster-${new Date().toISOString().slice(0, 10)}.pdf`,
        title: course.title,
        sessionLabel: isLive && stamp ? `Live Synchronous Session  -  ${stamp}` : '',
        providerLine: PROVIDER_LINE,
        ceuHours: course.ceu_hours,
        agencies,
        records,
        generatedOn: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
      });
    } catch (err) {
      console.error('Roster PDF export failed', err);
      alert('Could not generate the PDF. Please try again.');
    } finally {
      setExportingPdf(false);
    }
  };

  const exportAttendance = () => {
    // No-show participants are intentionally left off the attendance report —
    // it reflects who actually attended, not enrolled-but-absent students.
    const rows = attendance.map((a) => ({
      zoom_name: a.zoom_display_name || '',
      zoom_email: a.zoom_user_email,
      matched_user: resolveMatchedName(a) || '',
      matched_email: a.user_email || '',
      join_time: a.join_time ? new Date(a.join_time).toLocaleString() : '',
      leave_time: a.leave_time ? new Date(a.leave_time).toLocaleString() : '',
      duration_minutes: a.duration_minutes,
    }));
    downloadCsv(rows, `${course.slug}-zoom-attendance-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  // ── Evaluation helpers ──────────────────────────────────
  const questions = course.evaluation_questions || [];
  const questionMap = Object.fromEntries(questions.map(q => [q.id, q]));

  // Group responses by student (enrollment_id)
  const studentEvals = {};
  for (const r of evalResponses) {
    if (!studentEvals[r.enrollment_id]) {
      studentEvals[r.enrollment_id] = { name: r.full_name, email: r.email, responses: {} };
    }
    studentEvals[r.enrollment_id].responses[r.question_id] = r;
  }
  const studentEvalList = Object.entries(studentEvals).map(([eid, data]) => ({ enrollment_id: eid, ...data }));

  // ── Credential (multi_select_text) helpers ──────────────
  // The roster surfaces credentialing affiliations + per-agency certification
  // numbers sourced from the first multi_select_text evaluation question.
  const credentialQuestion = questions.find((q) => q.type === 'multi_select_text') || null;
  const credentialOptions = credentialQuestion?.options || [];
  const credentialByEnrollment = {};
  if (credentialQuestion) {
    for (const r of evalResponses) {
      if (r.question_id !== credentialQuestion.id) continue;
      try {
        const p = JSON.parse(r.response_value) || {};
        credentialByEnrollment[r.enrollment_id] = { selected: p.selected || [], values: p.values || {} };
      } catch { /* ignore malformed */ }
    }
  }
  const credForEnrollment = (eid) => credentialByEnrollment[eid] || { selected: [], values: {} };

  // ── Roster filtering ────────────────────────────────────
  // "Completed + met minimums": completed enrollment AND (for live courses)
  // attendance verified. Non-live courses have no attendance minimum.
  const isCompletedVerified = (s) =>
    s.status === 'completed' && (!isLive || verifiedUserIds.has(s.user_id));
  const rosterStudents = rosterOnlyCompleted ? students.filter(isCompletedVerified) : students;

  // In-person seat usage. Counted across all enrollments, not the filtered
  // roster, so the figure reflects the room rather than the current view.
  const inPersonCount = students.filter((s) => s.attendance_mode === 'in_person').length;

  // Which required windows each attendee has covered. "Morning but not
  // afternoon" is exactly what the windows exist to surface, so it gets its own
  // state on the roster rather than reading as plain unverified.
  const requiredWindows = checkinWindows.filter((w) => w.required);
  const checkinsByEnrollment = {};
  for (const c of checkins) {
    (checkinsByEnrollment[c.enrollment_id] ||= new Set()).add(c.window_id);
  }
  const coverageFor = (s) => {
    if (!requiredWindows.length) return null;
    const got = checkinsByEnrollment[s.enrollment_id] || new Set();
    return { done: requiredWindows.filter((w) => got.has(w.id)).length, total: requiredWindows.length };
  };
  const openWindow = checkinWindows.find((w) => w.state === 'open') || null;
  const capacity = course.in_person_capacity;
  const overCapacity = capacity != null && inPersonCount > capacity;

  // ── Attendance ↔ enrollee reconciliation ────────────────
  // Students who join as Zoom guests produce attendance rows that aren't linked
  // to their Learnable user_id (user_id NULL). Match those rows back to enrolled
  // students by email or display name so (a) their real duration shows under
  // their name, and (b) they aren't falsely listed as "No record" no-shows.
  const norm = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const studentByEmail = {};
  const studentByName = {};
  for (const s of students) {
    if (s.email) studentByEmail[norm(s.email)] = s;
    if (s.full_name) studentByName[norm(s.full_name)] = s;
  }
  // Resolve an attendance row to an enrolled student name (matched id first,
  // then email, then display name). Returns null if it truly can't be linked.
  const resolveMatchedName = (a) => {
    if (a.user_name) return a.user_name;
    const s = studentByEmail[norm(a.zoom_user_email)] || studentByName[norm(a.zoom_display_name)];
    return s ? s.full_name : null;
  };
  // Identifiers that actually appear in captured attendance (any row).
  const attendedEmails = new Set(attendance.map((a) => norm(a.zoom_user_email)).filter(Boolean));
  const attendedNames = new Set(attendance.map((a) => norm(a.zoom_display_name)).filter(Boolean));
  // A backend no-show is only a *real* no-show if no attendance row matches its
  // email or name either.
  // Someone attending in person is absent from the Zoom report by design, so
  // they are not a no-show — they are either checked in or still awaiting it.
  const noShowCandidates = noShows.filter(
    (n) => !attendedEmails.has(norm(n.user_email)) && !attendedNames.has(norm(n.user_name)),
  );
  const realNoShows = noShowCandidates.filter((n) => n.attendance_mode !== 'in_person');
  const awaitingCheckIn = noShowCandidates.filter(
    (n) => n.attendance_mode === 'in_person' && !n.attendance_verified_at,
  );

  // Best-known attended minutes for an enrolled student (matched id, email, or
  // display name). Returns null if no attendance row can be attributed to them.
  const minAtt = course.min_attendance_minutes || 30;
  const attendanceMinsFor = (s) => {
    let best = null;
    for (const a of attendance) {
      const matches =
        a.user_id === s.user_id ||
        (s.email && norm(a.zoom_user_email) === norm(s.email)) ||
        (s.full_name && norm(a.zoom_display_name) === norm(s.full_name));
      if (matches) best = Math.max(best ?? 0, a.duration_minutes || 0);
    }
    return best;
  };
  // Enrolled students who attended a substantial-but-below-minimum amount — they
  // don't qualify for credit but an instructor should follow up.
  const partialAttendees = students
    .map((s) => ({ s, mins: attendanceMinsFor(s) }))
    .filter(({ s, mins }) => mins != null && mins < minAtt && !verifiedUserIds.has(s.user_id));

  // Aggregate stats per likert question
  const likertAggregates = questions
    .filter(q => q.type === 'likert')
    .map(q => {
      const values = evalResponses
        .filter(r => r.question_id === q.id && r.question_type === 'likert')
        .map(r => parseInt(r.response_value, 10))
        .filter(v => !isNaN(v));
      const dist = [0, 0, 0, 0, 0]; // index 0 = rating 1, index 4 = rating 5
      values.forEach(v => { if (v >= 1 && v <= 5) dist[v - 1]++; });
      const avg = values.length ? (values.reduce((a, b) => a + b, 0) / values.length) : 0;
      return { question: q, values, avg, dist, count: values.length };
    });

  const multiSelectAggregates = questions
    .filter(q => q.type === 'multi_select')
    .map(q => {
      const allSelected = evalResponses
        .filter(r => r.question_id === q.id && r.question_type === 'multi_select')
        .flatMap(r => { try { return JSON.parse(r.response_value); } catch { return []; } });
      const optionCounts = {};
      for (const opt of (q.options || [])) optionCounts[opt] = 0;
      const otherResponses = [];
      for (const s of allSelected) {
        if (optionCounts[s] !== undefined) { optionCounts[s]++; }
        else if (typeof s === 'string' && s.startsWith('Other: ')) { otherResponses.push(s.slice(7)); }
      }
      const respondentCount = evalResponses.filter(r => r.question_id === q.id && r.question_type === 'multi_select').length;
      return { question: q, optionCounts, respondentCount, otherResponses };
    });

  const yesNoAggregates = questions
    .filter(q => q.type === 'yes_no')
    .map(q => {
      const rs = evalResponses
        .filter(r => r.question_id === q.id && r.question_type === 'yes_no')
        .map(r => { try { return JSON.parse(r.response_value); } catch { return null; } })
        .filter(Boolean);
      const yes = rs.filter(x => x.choice === 'Yes').length;
      const no = rs.filter(x => x.choice === 'No').length;
      const explanations = rs.map(x => x.explanation).filter(t => t && t.trim());
      return { question: q, yes, no, total: yes + no, explanations };
    });

  const credentialAggregates = questions
    .filter(q => q.type === 'multi_select_text')
    .map(q => {
      const rs = evalResponses
        .filter(r => r.question_id === q.id && r.question_type === 'multi_select_text')
        .map(r => { try { return JSON.parse(r.response_value); } catch { return null; } })
        .filter(Boolean);
      const optionCounts = {};
      const optionNumbers = {};
      for (const opt of (q.options || [])) { optionCounts[opt] = 0; optionNumbers[opt] = []; }
      for (const p of rs) {
        for (const opt of (p.selected || [])) {
          if (optionCounts[opt] === undefined) continue;
          optionCounts[opt]++;
          const num = p.values?.[opt];
          if (num && String(num).trim()) optionNumbers[opt].push(String(num).trim());
        }
      }
      return { question: q, optionCounts, optionNumbers, respondentCount: rs.length };
    });

  const exportEvaluations = () => {
    const rows = [];
    for (const s of studentEvalList) {
      const row = { name: s.name, email: s.email };
      for (const q of questions) {
        const r = s.responses[q.id];
        const label = q.prompt.slice(0, 50);
        if (r && q.type === 'multi_select') {
          try { row[label] = JSON.parse(r.response_value).join('; '); } catch { row[label] = r.response_value; }
        } else if (r && q.type === 'multi_select_text') {
          try {
            const p = JSON.parse(r.response_value) || {};
            row[label] = (p.selected || [])
              .map((opt) => (p.values?.[opt] ? `${opt}: ${p.values[opt]}` : opt))
              .join('; ');
          } catch { row[label] = r.response_value; }
        } else if (r && q.type === 'yes_no') {
          try { const v = JSON.parse(r.response_value); row[label] = v.explanation ? `${v.choice} — ${v.explanation}` : (v.choice || ''); }
          catch { row[label] = r.response_value; }
        } else {
          row[label] = r?.response_value || '';
        }
      }
      rows.push(row);
    }
    downloadCsv(rows, `${course.slug}-evaluations-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const STATUS_BADGE = {
    not_started: 'bg-slate-100 text-slate-600',
    in_progress: 'bg-blue-100 text-blue-700',
    completed: 'bg-emerald-100 text-emerald-700',
  };

  const fmtTime = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const fmtDateTime = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  };

  return (
    <div className="p-4 lg:p-8 max-w-[1400px] mx-auto">
      <Link to={backTo} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft size={14} /> {backLabel}
      </Link>

      <div className="mb-6">
        <h1 className="text-2xl font-bold">{course.title}</h1>
        <p className="text-sm text-slate-600 mt-1">
          {course.course_type === 'hybrid' ? 'Hybrid event' : course.course_type === 'live' ? 'Live course' : course.course_type === 'tutorial' ? 'Tutorial' : 'Self-paced course'}
          {' · '}{students.length} student{students.length !== 1 ? 's' : ''}
          {isLive && ` · ${attendance.length} Zoom participant${attendance.length !== 1 ? 's' : ''}`}
        </p>
      </div>

      {(isLive || isEngine) && (
        <div className="flex gap-1 mb-6">
          {TABS.filter((t) =>
            isEngine ? t.key === 'roster' || t.key === 'progress' : t.key !== 'progress' && (t.key !== 'checkins' || course.conference_checkin),
          ).map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                tab === t.key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-200'
              }`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>
      )}

      {tab === 'progress' && isEngine && <EngineProgressTab courseId={course.id} courseTitle={course.title} />}

      {tab === 'roster' && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <h2 className="font-semibold">Student Roster</h2>
              <span className="text-xs text-slate-400">{rosterStudents.length} of {students.length}</span>
              {isHybrid && (
                <span
                  className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-md ${
                    overCapacity
                      ? 'bg-rose-100 text-rose-700'
                      : capacity != null && inPersonCount >= capacity
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-slate-100 text-slate-600'
                  }`}
                  title={capacity != null
                    ? `${inPersonCount} of ${capacity} in-person seats taken`
                    : 'In-person attendees (no capacity set)'}
                >
                  <MapPin size={12} />
                  {capacity != null
                    ? `${inPersonCount}/${capacity} in person`
                    : `${inPersonCount} in person`}
                  {overCapacity && ' — over capacity'}
                  {!overCapacity && capacity != null && inPersonCount >= capacity && ' — full'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={rosterOnlyCompleted}
                  onChange={(e) => setRosterOnlyCompleted(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Only completed{isLive ? ' & attendance-verified' : ''}
              </label>
              <button
                onClick={exportRosterPdf}
                disabled={!rosterStudents.length || exportingPdf}
                className="inline-flex items-center gap-2 text-xs bg-brand hover:bg-brand-dark text-white px-3 py-1.5 rounded-md font-medium disabled:opacity-50"
              >
                {exportingPdf ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
                {exportingPdf ? 'Generating…' : 'Export PDF'}
              </button>
              <button
                onClick={exportRoster}
                disabled={!rosterStudents.length}
                className="inline-flex items-center gap-2 text-xs bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-md font-medium disabled:opacity-50"
              >
                <Download size={12} /> Export CSV
              </button>
            </div>
          </div>
          {rosterStudents.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {students.length === 0
                ? 'No students enrolled yet.'
                : `No students have completed${isLive ? ' and met the attendance minimum' : ''} yet. Uncheck the filter to see all enrollees.`}
            </div>
          ) : (
            <div ref={rosterTableRef} className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold">Name</th>
                    <th className="text-left px-3 py-3 font-semibold">Email</th>
                    <th className="text-left px-3 py-3 font-semibold">Status</th>
                    <th className="text-right px-3 py-3 font-semibold">Progress</th>
                    <th className="text-left px-3 py-3 font-semibold hidden lg:table-cell">Enrolled</th>
                    <th className="text-left px-3 py-3 font-semibold hidden lg:table-cell">Completed</th>
                    <th className="text-left px-5 py-3 font-semibold hidden xl:table-cell">Certificate</th>
                    {credentialQuestion && <th className="text-left px-3 py-3 font-semibold">Affiliations</th>}
                    {credentialQuestion && credentialOptions.map((opt) => (
                      <th key={opt} className="text-left px-3 py-3 font-semibold">{opt} #</th>
                    ))}
                    {isHybrid && <th className="text-left px-3 py-3 font-semibold">Attending</th>}
                    {isLive && (
                      <th className={`text-right px-5 py-3 font-semibold ${STICKY_CELL} ${STICKY_HEAD_BG} ${rosterShadowed ? STICKY_SHADOW : ''}`}>
                        Attendance
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rosterStudents.map((s) => (
                    <tr key={s.enrollment_id} className="group hover:bg-slate-50">
                      <td className="px-5 py-3 font-medium text-slate-900">{s.full_name}</td>
                      <td className="px-3 py-3 text-slate-600">{s.email}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${STATUS_BADGE[s.status] || 'bg-slate-100 text-slate-600'}`}>
                          {isLive && s.status === 'not_started'
                            ? (sessionStarted && attendanceMinsFor(s) == null ? 'no-show' : 'not started')
                            : s.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums">{s.progress_percent}%</td>
                      <td className="px-3 py-3 hidden lg:table-cell text-slate-500 text-xs">{formatDate(s.enrolled_at)}</td>
                      <td className="px-3 py-3 hidden lg:table-cell text-slate-500 text-xs">{s.completed_at ? formatDate(s.completed_at) : '-'}</td>
                      <td className="px-5 py-3 hidden xl:table-cell text-xs">
                        {s.certificate_number ? (
                          <a href={`/cert/${s.certificate_number}`} target="_blank" rel="noreferrer" className="text-brand hover:underline font-mono">
                            {s.certificate_number}
                          </a>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      {credentialQuestion && (() => {
                        const cred = credForEnrollment(s.enrollment_id);
                        return (
                          <>
                            <td className="px-3 py-3 text-xs text-slate-600">
                              {cred.selected.length ? cred.selected.join(', ') : <span className="text-slate-400">—</span>}
                            </td>
                            {credentialOptions.map((opt) => (
                              <td key={opt} className="px-3 py-3 text-xs font-mono text-slate-700">
                                {cred.values?.[opt]
                                  ? cred.values[opt]
                                  : <span className="text-slate-400">{cred.selected.includes(opt) ? '—' : ''}</span>}
                              </td>
                            ))}
                          </>
                        );
                      })()}
                      {isHybrid && (
                        <td className="px-3 py-3">
                          <select
                            value={s.attendance_mode || 'virtual'}
                            onChange={(e) => setAttendanceMode(s.enrollment_id, e.target.value, s.full_name)}
                            className="text-xs border border-slate-200 rounded-md px-1.5 py-1 bg-white hover:border-slate-300 focus:outline-none focus:border-brand"
                            title="Change how this person is attending"
                          >
                            <option value="virtual">Online</option>
                            <option value="in_person">In person</option>
                          </select>
                        </td>
                      )}
                      {isLive && (
                        <td className={`px-5 py-3 text-right whitespace-nowrap ${STICKY_CELL} ${STICKY_CELL_BG} ${rosterShadowed ? STICKY_SHADOW : ''}`}>
                          {verifiedUserIds.has(s.user_id) ? (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium" title={
                              s.checkin_method === 'self' ? 'Checked in with the event code'
                              : s.checkin_method === 'admin' ? 'Checked in by an admin'
                              : 'Verified from the Zoom attendance report'
                            }>
                              <CheckSquare size={13} /> Verified
                              {s.checkin_method && (
                                <span className="text-slate-400 font-normal">
                                  ({s.checkin_method === 'self' ? 'self check-in' : 'admin check-in'})
                                </span>
                              )}
                            </span>
                          ) : s.attendance_mode === 'in_person' ? (
                            // No Zoom record will ever exist for someone in the
                            // room — checking in is the only way they verify.
                            (() => {
                              const cov = coverageFor(s);
                              return (
                                <div className="inline-flex items-center gap-2 justify-end">
                                  {cov && cov.done > 0 && (
                                    <span className="inline-flex items-center gap-1 text-xs font-semibold tabular-nums text-amber-600"
                                          title={`Checked into ${cov.done} of ${cov.total} required windows`}>
                                      <Clock size={12} /> {cov.done}/{cov.total}
                                    </span>
                                  )}
                                  <button
                                    onClick={() => checkIn(s.user_id, s.full_name, openWindow?.id || null)}
                                    disabled={verifyingId === s.user_id}
                                    className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg font-medium border border-brand/30 text-brand hover:bg-brand/5 transition disabled:opacity-50"
                                    title={openWindow ? `Check in for "${openWindow.label}"` : 'Check this person in'}
                                  >
                                    <CheckSquare size={13} /> {verifyingId === s.user_id ? 'Checking in…' : 'Check in'}
                                  </button>
                                </div>
                              );
                            })()
                          ) : (() => {
                            const mins = attendanceMinsFor(s);
                            // Met the minimum but still unverified means the sync could not
                            // tie this participant to the account (different Zoom email, and
                            // a display name that wasn't an unambiguous match). They are not
                            // short of credit — they just need a human to confirm identity.
                            const met = mins != null && mins >= minAtt;
                            return (
                              <div className="inline-flex items-center gap-2 justify-end">
                                {mins != null && (
                                  <span
                                    className={`inline-flex items-center gap-1 text-xs font-semibold tabular-nums ${met ? 'text-slate-500' : 'text-amber-600'}`}
                                    title={met
                                      ? `Attended ${mins} of ${minAtt} min required — minimum met, but not matched to this account; verify to release credit`
                                      : `Attended ${mins} of ${minAtt} min required — short of credit, follow up`}
                                  >
                                    <Clock size={12} /> {mins}/{minAtt} min
                                  </span>
                                )}
                                <button
                                  onClick={() => verifyAttendance(s.user_id, s.full_name)}
                                  disabled={verifyingId === s.user_id}
                                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg font-medium border border-brand/30 text-brand hover:bg-brand/5 transition disabled:opacity-50"
                                  title="Manually verify attendance (overrides the Zoom check)"
                                >
                                  <CheckSquare size={13} /> {verifyingId === s.user_id ? 'Verifying…' : 'Verify'}
                                </button>
                              </div>
                            );
                          })()}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'attendance' && isLive && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-semibold">Zoom Session Attendance</h2>
            <button
              onClick={exportAttendance}
              disabled={!attendance.length}
              className="inline-flex items-center gap-2 text-xs bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-md font-medium disabled:opacity-50"
            >
              <Download size={12} /> Export CSV
            </button>
          </div>
          {partialAttendees.length > 0 && (
            <div className="mx-5 mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-sm font-semibold text-amber-800">
                {partialAttendees.length} student{partialAttendees.length !== 1 ? 's' : ''} attended but fell short of the {minAtt}-min requirement — follow up
              </p>
              <p className="text-xs text-amber-700 mt-0.5 mb-2">Substantial time in session but not enough for credit.</p>
              <div className="flex flex-wrap gap-2">
                {partialAttendees.map(({ s, mins }) => (
                  <span key={s.user_id} className="inline-flex items-center gap-1.5 bg-white border border-amber-200 rounded-full px-2.5 py-1 text-xs">
                    <span className="font-medium text-slate-800">{s.full_name}</span>
                    <span className="text-amber-700 font-semibold tabular-nums">{mins}/{minAtt} min</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {attendance.length === 0 && realNoShows.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              No Zoom attendance data yet.
              {!course.zoom_meeting_id && <span className="block mt-1 text-xs">No Zoom meeting ID configured for this course.</span>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold">Participant</th>
                    <th className="text-left px-3 py-3 font-semibold">Zoom Email</th>
                    <th className="text-left px-3 py-3 font-semibold hidden md:table-cell">Matched User</th>
                    <th className="text-left px-3 py-3 font-semibold">
                      <span className="inline-flex items-center gap-1"><LogIn size={12} /> Joined</span>
                    </th>
                    <th className="text-left px-3 py-3 font-semibold">
                      <span className="inline-flex items-center gap-1"><LogOut size={12} /> Left</span>
                    </th>
                    <th className="text-right px-5 py-3 font-semibold">
                      <span className="inline-flex items-center gap-1"><Clock size={12} /> Duration</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {attendance.map((a) => {
                    const meetsMin = a.duration_minutes >= (course.min_attendance_minutes || 30);
                    return (
                      <tr key={a.id} className="hover:bg-slate-50">
                        <td className="px-5 py-3 font-medium text-slate-900">{a.zoom_display_name || '-'}</td>
                        <td className="px-3 py-3 text-slate-600 text-xs">
                          {a.zoom_user_email?.endsWith('@noemail.local')
                            ? <span className="text-slate-400 italic">guest (no email)</span>
                            : a.zoom_user_email}
                        </td>
                        <td className="px-3 py-3 hidden md:table-cell">
                          {(() => {
                            const resolved = resolveMatchedName(a);
                            if (a.user_name) return <span className="text-slate-900">{resolved}</span>;
                            if (resolved) return <span className="text-slate-900">{resolved} <span className="text-amber-600 text-xs font-medium">(by name)</span></span>;
                            return <span className="text-amber-600 text-xs font-medium">Unmatched</span>;
                          })()}
                        </td>
                        <td className="px-3 py-3 text-slate-600 tabular-nums text-xs">{fmtDateTime(a.join_time)}</td>
                        <td className="px-3 py-3 text-slate-600 tabular-nums text-xs">{fmtDateTime(a.leave_time)}</td>
                        <td className="px-5 py-3 text-right">
                          <span className={`inline-flex items-center gap-1.5 font-semibold tabular-nums ${meetsMin ? 'text-emerald-600' : 'text-rose-600'}`}>
                            <Clock size={13} />
                            {a.duration_minutes} min
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {awaitingCheckIn.map((n) => (
                    <tr key={`checkin-${n.enrollment_id}`} className="bg-amber-50/40 hover:bg-amber-50">
                      <td className="px-5 py-3 font-medium text-slate-900">{n.user_name}</td>
                      <td className="px-3 py-3 text-slate-400 text-xs">in person</td>
                      <td className="px-3 py-3 hidden md:table-cell">
                        <span className="text-slate-900">{n.user_name}</span>
                      </td>
                      <td className="px-3 py-3 text-slate-400 text-xs">—</td>
                      <td className="px-3 py-3 text-slate-400 text-xs">—</td>
                      <td className="px-5 py-3 text-right">
                        <span className="inline-flex items-center gap-1.5 font-semibold text-amber-600">
                          <Clock size={13} /> Awaiting check-in
                        </span>
                      </td>
                    </tr>
                  ))}
                  {realNoShows.map((n) => (
                    <tr key={`noshow-${n.enrollment_id}`} className="bg-rose-50/40 hover:bg-rose-50">
                      <td className="px-5 py-3 font-medium text-slate-900">{n.user_name}</td>
                      <td className="px-3 py-3 text-slate-400 text-xs">—</td>
                      <td className="px-3 py-3 hidden md:table-cell">
                        <span className="text-slate-900">{n.user_name}</span>
                      </td>
                      <td className="px-3 py-3 text-slate-400 text-xs">—</td>
                      <td className="px-3 py-3 text-slate-400 text-xs">—</td>
                      <td className="px-5 py-3 text-right">
                        <span className="inline-flex items-center gap-1.5 font-semibold tabular-nums text-rose-600">
                          <Clock size={13} /> No record
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-5 py-3 border-t border-slate-100 text-xs text-slate-500">
                Minimum required: {course.min_attendance_minutes || 30} minutes.
                <span className="text-emerald-600 font-semibold ml-2">Green</span> = meets requirement,
                <span className="text-rose-600 font-semibold ml-2">Red</span> = below minimum.
                {awaitingCheckIn.length > 0 && (
                  <span className="block mt-1">
                    <span className="text-amber-600 font-semibold">Awaiting check-in</span> = registered
                    in person, so they never appear in the Zoom report. Check them in from the Roster tab.
                    {' '}{awaitingCheckIn.length} student{awaitingCheckIn.length !== 1 ? 's' : ''}.
                  </span>
                )}
                {realNoShows.length > 0 && (
                  <span className="block mt-1">
                    <span className="text-rose-600 font-semibold">No record</span> = enrolled but no Zoom attendance captured. {realNoShows.length} student{realNoShows.length !== 1 ? 's' : ''}.
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Evaluations Tab ──────────────────────────────────── */}
      {tab === 'evaluations' && isLive && (
        <div className="space-y-6">

          {/* Send Evaluation Reminders — pending = attendance-verified (eligible) but
              not yet submitted, matching exactly who the reminder targets. */}
          {(() => {
            const pending = flowProgress
              .filter((p) => p.attendance_verified_at && !p.evaluation_submitted_at)
              .map((p) => ({ user_id: p.user_id, full_name: p.full_name, email: p.email }))
              .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
            const pendingCount = pending.length;
            if (pendingCount === 0 && !evalReminderResult) return null;
            return (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-5 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="text-sm text-amber-800">
                    {evalReminderResult ? (
                      <span>{evalReminderResult.sent} reminder{evalReminderResult.sent !== 1 ? 's' : ''} sent to students with pending evaluations.</span>
                    ) : (
                      <span><strong>{pendingCount}</strong> student{pendingCount !== 1 ? 's' : ''} attended but {pendingCount !== 1 ? "haven't" : "hasn't"} submitted an evaluation.</span>
                    )}
                  </div>
                  <button
                    onClick={async () => {
                      setSendingEvalReminders(true);
                      setEvalReminderResult(null);
                      try {
                        const res = await api(`/api/admin/courses/${id}/send-eval-reminders`, { method: 'POST' });
                        setEvalReminderResult(res);
                      } catch (err) {
                        setEvalReminderResult({ sent: 0, error: err.message });
                      } finally {
                        setSendingEvalReminders(false);
                      }
                    }}
                    disabled={sendingEvalReminders || pendingCount === 0}
                    className="shrink-0 inline-flex items-center gap-2 text-xs bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-md font-semibold disabled:opacity-50 transition"
                  >
                    {sendingEvalReminders ? <Loader2 size={12} className="animate-spin" /> : <ClipboardList size={12} />}
                    Send Reminder{pendingCount !== 1 ? 's' : ''}
                  </button>
                </div>
                {!evalReminderResult && pendingCount > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {pending.map((p) => (
                      <a
                        key={p.user_id}
                        href={`mailto:${p.email}`}
                        title={p.email}
                        className="inline-flex items-center gap-1.5 bg-white border border-amber-200 rounded-full px-2.5 py-1 text-xs text-slate-800 hover:border-amber-400 transition"
                      >
                        <span className="font-medium">{p.full_name}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Aggregate — Likert Questions */}
          {likertAggregates.length > 0 && (
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                <h2 className="font-semibold flex items-center gap-2">
                  <Star size={15} className="text-amber-500" /> Course Averages
                </h2>
                <span className="text-xs text-slate-500">
                  {studentEvalList.length} response{studentEvalList.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="divide-y divide-slate-100">
                {likertAggregates.map((agg) => (
                  <div key={agg.question.id} className="px-5 py-4">
                    <p className="text-sm font-medium text-slate-800 mb-3">{agg.question.prompt}</p>
                    <div className="flex items-center gap-6">
                      {/* Average score badge */}
                      <div className="text-center shrink-0">
                        <div className={`text-3xl font-bold tabular-nums ${
                          agg.avg >= 4 ? 'text-emerald-600' : agg.avg >= 3 ? 'text-amber-600' : 'text-rose-600'
                        }`}>
                          {agg.avg.toFixed(1)}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mt-0.5">
                          avg of {agg.count}
                        </div>
                      </div>

                      {/* Distribution bars — hover for names, click to list who answered */}
                      <div className="flex-1 space-y-1">
                        {[5, 4, 3, 2, 1].map((rating) => {
                          const count = agg.dist[rating - 1];
                          const pct = agg.count ? (count / agg.count) * 100 : 0;
                          const key = `${agg.question.id}:${rating}`;
                          const expanded = expandedBars.has(key);
                          const responders = count
                            ? studentEvalList.filter((s) => {
                                const r = s.responses[agg.question.id];
                                return r && parseInt(r.response_value, 10) === rating;
                              })
                            : [];
                          return (
                            <div key={rating}>
                              <button
                                type="button"
                                onClick={() => count && toggleBar(key)}
                                disabled={!count}
                                title={responders.length ? responders.map((s) => s.name).join(', ') : undefined}
                                aria-expanded={expanded}
                                className={`w-full flex items-center gap-2 text-xs rounded px-1 -mx-1 text-left ${count ? 'cursor-pointer hover:bg-slate-50' : 'cursor-default'} ${expanded ? 'bg-slate-50' : ''}`}
                              >
                                <span className="w-3 text-right text-slate-500 font-medium tabular-nums">{rating}</span>
                                <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full transition-all ${
                                      rating >= 4 ? 'bg-emerald-400' : rating === 3 ? 'bg-amber-400' : 'bg-rose-400'
                                    }`}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                                <span className="w-6 text-right text-slate-400 tabular-nums">{count}</span>
                              </button>
                              {expanded && responders.length > 0 && (
                                <div className="flex flex-wrap gap-1 pl-5 pr-8 pt-1.5 pb-1">
                                  {responders.map((s) => (
                                    <span
                                      key={s.enrollment_id}
                                      title={s.email}
                                      className="inline-block bg-white border border-slate-200 text-slate-600 text-[11px] px-2 py-0.5 rounded-full"
                                    >
                                      {s.name}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {/* Likert scale legend */}
                    <div className="flex justify-between text-[10px] text-slate-400 mt-2 px-0">
                      <span>Strongly Disagree</span>
                      <span>Strongly Agree</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Aggregate — Multi-Select Questions */}
          {multiSelectAggregates.length > 0 && (
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-200">
                <h2 className="font-semibold flex items-center gap-2">
                  <CheckSquare size={15} className="text-brand" /> Multi-Select Summary
                </h2>
              </div>
              <div className="divide-y divide-slate-100">
                {multiSelectAggregates.map((agg) => (
                  <div key={agg.question.id} className="px-5 py-4">
                    <p className="text-sm font-medium text-slate-800 mb-1">{agg.question.prompt}</p>
                    <p className="text-xs text-slate-400 mb-3">{agg.respondentCount} respondent{agg.respondentCount !== 1 ? 's' : ''}</p>
                    <div className="space-y-1.5">
                      {(agg.question.options || []).map((opt) => {
                        const count = agg.optionCounts[opt] || 0;
                        const pct = agg.respondentCount ? (count / agg.respondentCount) * 100 : 0;
                        return (
                          <div key={opt} className="flex items-center gap-2 text-xs">
                            <span className="w-32 text-right text-slate-600 font-medium truncate" title={opt}>{opt}</span>
                            <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-brand/60 transition-all" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="w-12 text-right text-slate-400 tabular-nums">{count} ({Math.round(pct)}%)</span>
                          </div>
                        );
                      })}
                      {agg.otherResponses.length > 0 && (
                        <>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="w-32 text-right text-slate-600 font-medium">Other</span>
                            <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-amber-400/60 transition-all" style={{ width: `${agg.respondentCount ? (agg.otherResponses.length / agg.respondentCount) * 100 : 0}%` }} />
                            </div>
                            <span className="w-12 text-right text-slate-400 tabular-nums">{agg.otherResponses.length} ({Math.round(agg.respondentCount ? (agg.otherResponses.length / agg.respondentCount) * 100 : 0)}%)</span>
                          </div>
                          <div className="ml-34 pl-1 mt-1">
                            {agg.otherResponses.map((t, i) => (
                              <span key={i} className="inline-block bg-amber-50 text-amber-800 text-xs px-2 py-0.5 rounded-full mr-1 mb-1">{t || '(blank)'}</span>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Yes / No Summary */}
          {yesNoAggregates.length > 0 && (
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-200">
                <h2 className="font-semibold flex items-center gap-2">
                  <CheckSquare size={15} className="text-brand" /> Yes / No Summary
                </h2>
              </div>
              <div className="divide-y divide-slate-100">
                {yesNoAggregates.map((agg) => (
                  <div key={agg.question.id} className="px-5 py-4">
                    <p className="text-sm font-medium text-slate-800 mb-1">{agg.question.prompt}</p>
                    <p className="text-xs text-slate-400 mb-3">{agg.total} response{agg.total !== 1 ? 's' : ''}</p>
                    <div className="flex items-center gap-4 mb-3">
                      <span className="text-sm"><span className="font-bold text-emerald-600 tabular-nums">{agg.yes}</span> <span className="text-slate-500">Yes</span></span>
                      <span className="text-sm"><span className="font-bold text-rose-600 tabular-nums">{agg.no}</span> <span className="text-slate-500">No</span></span>
                      {agg.total > 0 && (
                        <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden flex">
                          <div className="h-full bg-emerald-400" style={{ width: `${(agg.yes / agg.total) * 100}%` }} />
                          <div className="h-full bg-rose-400" style={{ width: `${(agg.no / agg.total) * 100}%` }} />
                        </div>
                      )}
                    </div>
                    {agg.explanations.length > 0 && (
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Explanations</p>
                        <ul className="space-y-1">
                          {agg.explanations.map((t, i) => (
                            <li key={i} className="text-sm text-slate-700 bg-slate-50 rounded px-3 py-1.5">{t}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Credentialing Summary (multi-select + number) */}
          {credentialAggregates.length > 0 && (
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-200">
                <h2 className="font-semibold flex items-center gap-2">
                  <CheckSquare size={15} className="text-brand" /> Credentialing Summary
                </h2>
              </div>
              <div className="divide-y divide-slate-100">
                {credentialAggregates.map((agg) => (
                  <div key={agg.question.id} className="px-5 py-4">
                    <p className="text-sm font-medium text-slate-800 mb-1">{agg.question.prompt}</p>
                    <p className="text-xs text-slate-400 mb-3">{agg.respondentCount} respondent{agg.respondentCount !== 1 ? 's' : ''}</p>
                    <div className="space-y-3">
                      {(agg.question.options || []).map((opt) => {
                        const count = agg.optionCounts[opt] || 0;
                        const nums = agg.optionNumbers[opt] || [];
                        const pct = agg.respondentCount ? (count / agg.respondentCount) * 100 : 0;
                        return (
                          <div key={opt}>
                            <div className="flex items-center gap-2 text-xs">
                              <span className="w-32 text-right text-slate-600 font-medium truncate" title={opt}>{opt}</span>
                              <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full rounded-full bg-brand/60 transition-all" style={{ width: `${pct}%` }} />
                              </div>
                              <span className="w-12 text-right text-slate-400 tabular-nums">{count} ({Math.round(pct)}%)</span>
                            </div>
                            {nums.length > 0 && (
                              <div className="ml-34 pl-1 mt-1 flex flex-wrap gap-1">
                                {nums.map((t, i) => (
                                  <span key={i} className="inline-block bg-brand/10 text-brand text-xs font-mono px-2 py-0.5 rounded-full">{t}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-Student Responses Table */}
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-semibold">Individual Responses</h2>
              <button
                onClick={exportEvaluations}
                disabled={!studentEvalList.length}
                className="inline-flex items-center gap-2 text-xs bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-md font-medium disabled:opacity-50"
              >
                <Download size={12} /> Export CSV
              </button>
            </div>
            {studentEvalList.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No evaluation responses yet.</div>
            ) : (
              <div className="bg-slate-50 p-4 space-y-2">
                {studentEvalList.map((s) => {
                  const initials = (s.name || '?').trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
                  const likerts = questions.filter((q) => q.type === 'likert');
                  const others = questions.filter((q) => q.type !== 'likert');
                  const likertVals = likerts
                    .map((q) => s.responses[q.id])
                    .filter(Boolean)
                    .map((r) => parseInt(r.response_value, 10))
                    .filter((n) => !Number.isNaN(n));
                  const avg = likertVals.length ? likertVals.reduce((a, b) => a + b, 0) / likertVals.length : null;
                  const avgCls = avg == null ? 'text-slate-400' : avg >= 4 ? 'text-emerald-600' : avg >= 3 ? 'text-amber-600' : 'text-rose-600';
                  const open = expandedEvals.has(s.enrollment_id);
                  return (
                    <div key={s.enrollment_id} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleEval(s.enrollment_id)}
                        aria-expanded={open}
                        className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50 transition-colors"
                      >
                        <div className="w-10 h-10 rounded-full bg-brand/10 text-brand flex items-center justify-center font-semibold text-sm shrink-0">
                          {initials || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-slate-900 truncate">{s.name}</div>
                          <div className="text-xs text-slate-500 truncate">{s.email}</div>
                        </div>
                        {avg != null && (
                          <div className="text-right shrink-0">
                            <div className={`text-lg font-bold tabular-nums leading-none ${avgCls}`}>{avg.toFixed(1)}</div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mt-1">avg rating</div>
                          </div>
                        )}
                        <ChevronDown size={18} className={`text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                      </button>

                      {open && (
                        <div className="px-5 border-t border-slate-100 divide-y divide-slate-100">
                          {likerts.length > 0 && (
                            <div className="py-4">
                              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2.5">Ratings</p>
                              <div className="space-y-2.5">
                                {likerts.map((q) => {
                                  const r = s.responses[q.id];
                                  const v = r ? parseInt(r.response_value, 10) : null;
                                  const cls = v == null ? 'text-slate-300' : v >= 4 ? 'text-emerald-600' : v === 3 ? 'text-amber-600' : 'text-rose-600';
                                  return (
                                    <div key={q.id} className="flex items-center gap-3">
                                      <span className="flex-1 text-sm text-slate-700">{q.prompt}</span>
                                      {v == null ? (
                                        <span className="text-sm text-slate-300 shrink-0">No response</span>
                                      ) : (
                                        <div className="flex items-center gap-2 shrink-0">
                                          <LikertDots value={v} />
                                          <span className={`w-4 text-right text-sm font-semibold tabular-nums ${cls}`}>{v}</span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {others.map((q) => {
                            const r = s.responses[q.id];
                            let sel = [];
                            if (r && q.type === 'multi_select') { try { sel = JSON.parse(r.response_value); } catch {} }
                            return (
                              <div key={q.id} className="py-4">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">{q.prompt}</p>
                                {!r ? (
                                  <p className="text-sm text-slate-300 italic">No response</p>
                                ) : q.type === 'multi_select' ? (
                                  <div className="flex flex-wrap gap-1.5">
                                    {sel.map((opt) => (
                                      <span
                                        key={opt}
                                        className={`inline-block text-xs px-2.5 py-1 rounded-full ${opt.startsWith('Other: ') ? 'bg-amber-50 text-amber-800' : 'bg-brand/10 text-brand'}`}
                                      >
                                        {opt}
                                      </span>
                                    ))}
                                  </div>
                                ) : q.type === 'multi_select_text' ? (
                                  (() => {
                                    let p = { selected: [], values: {} };
                                    try { const x = JSON.parse(r.response_value) || {}; p = { selected: x.selected || [], values: x.values || {} }; } catch { /* */ }
                                    if (!p.selected.length) return <p className="text-sm text-slate-300 italic">None selected</p>;
                                    return (
                                      <div className="flex flex-wrap gap-1.5">
                                        {p.selected.map((opt) => (
                                          <span key={opt} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-brand/10 text-brand">
                                            {opt}
                                            {p.values?.[opt] && <span className="font-mono font-semibold">: {p.values[opt]}</span>}
                                          </span>
                                        ))}
                                      </div>
                                    );
                                  })()
                                ) : q.type === 'yes_no' ? (
                                  (() => {
                                    let v = {}; try { v = JSON.parse(r.response_value) || {}; } catch { /* */ }
                                    return (
                                      <div className="space-y-1.5">
                                        <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${v.choice === 'Yes' ? 'bg-emerald-100 text-emerald-700' : v.choice === 'No' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-500'}`}>{v.choice || '—'}</span>
                                        {v.explanation && <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{v.explanation}</p>}
                                      </div>
                                    );
                                  })()
                                ) : (
                                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{r.response_value}</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {questions.length === 0 && (
            <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-500">
              No evaluation questions configured for this course.
            </div>
          )}
        </div>
      )}

      {/* ── Recording Tab (admin-only) ─────────────────────────── */}
      {tab === 'recording' && isLive && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
            <h2 className="font-semibold flex items-center gap-2">
              <PlayCircle size={15} /> Session Recording
            </h2>
            <button
              onClick={loadRecording}
              disabled={recordingLoading}
              className="inline-flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-md font-medium disabled:opacity-50"
            >
              {recordingLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              {recordingUrl ? 'Reload' : 'Load recording'}
            </button>
          </div>
          <div className="p-5">
            <p className="text-xs text-slate-500 mb-4">
              Recordings are admin-only and are no longer shown to students. The link below is a time-limited Zoom URL.
            </p>
            {recordingError && (
              <div className="px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800 rounded text-sm">
                {recordingError}
              </div>
            )}
            {recordingUrl && (
              <div className="space-y-3">
                <video controls src={recordingUrl} className="w-full rounded-lg border border-slate-200 bg-black" />
                <a
                  href={recordingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm bg-slate-800 text-white px-4 py-2 rounded-lg font-medium hover:bg-slate-700"
                >
                  <ExternalLink size={14} /> Open / download recording
                </a>
              </div>
            )}
            {!recordingUrl && !recordingError && !recordingLoading && (
              <div className="text-center text-slate-500 py-8">
                Click “Load recording” to fetch the Zoom cloud recording.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Transcript Tab ─────────────────────────────────────── */}
      {tab === 'transcript' && isLive && (
        <AdminTranscriptTab courseId={id} courseSlug={course.slug} />
      )}

      {/* ── Door check-ins (conference check-in page) ──────────── */}
      {tab === 'checkins' && course.conference_checkin && (
        <DoorCheckinsTab
          courseId={id} students={students} windows={checkinWindows} onCredited={() => load(true)}
        />
      )}

    </div>
  );
}

// ── Likert dot visualization ────────────────────────────
function LikertDots({ value }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className={`w-2.5 h-2.5 rounded-full ${
            i <= value
              ? value >= 4 ? 'bg-emerald-400' : value === 3 ? 'bg-amber-400' : 'bg-rose-400'
              : 'bg-slate-200'
          }`}
        />
      ))}
    </div>
  );
}

// ── Admin Transcript Tab ───────────────────────────────
function AdminTranscriptTab({ courseId, courseSlug }) {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const searchTimer = useRef(null);

  const loadTranscript = () => {
    setLoading(true);
    api(`/api/admin/courses/${courseId}/transcript`)
      .then((d) => setSegments(d.segments || []))
      .catch(() => setSegments([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadTranscript(); }, [courseId]);

  const doSearch = (q) => {
    if (!q.trim()) { setSearchResults(null); return; }
    setSearching(true);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      api(`/api/admin/courses/${courseId}/transcript/search?q=${encodeURIComponent(q)}`)
        .then((d) => setSearchResults(d.results || []))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 300);
  };

  const handleSearch = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    doSearch(q);
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const res = await api(`/api/admin/courses/${courseId}/transcript/refresh`, { method: 'POST' });
      if (res.stored) loadTranscript();
    } catch (err) {
      console.error('Refresh failed:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const exportTranscript = () => {
    const rows = (searchResults || segments).map((s) => ({
      time: fmtMs(s.timestamp_start_ms),
      speaker: s.speaker || '',
      text: s.text,
    }));
    downloadCsv(rows, `${courseSlug}-transcript-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const fmtMs = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  if (loading) return <div className="p-8 text-slate-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading transcript…</div>;

  const display = searchResults || segments;

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
        <h2 className="font-semibold flex items-center gap-2">
          <FileText size={15} /> Session Transcript
          <span className="text-xs font-normal text-slate-400">({segments.length} segments)</span>
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-md font-medium disabled:opacity-50"
            title="Re-fetch transcript from Zoom"
          >
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            onClick={exportTranscript}
            disabled={!display.length}
            className="inline-flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-md font-medium disabled:opacity-50"
          >
            <Download size={12} /> Export CSV
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="px-5 py-2.5 border-b border-slate-100 flex items-center gap-2">
        <Search size={15} className="text-slate-400 shrink-0" />
        <input
          type="text"
          value={searchQuery}
          onChange={handleSearch}
          placeholder="Search transcript…"
          className="flex-1 text-sm border-none outline-none bg-transparent placeholder:text-slate-400"
        />
        {searchQuery && (
          <button onClick={() => { setSearchQuery(''); setSearchResults(null); }} className="text-slate-400 hover:text-slate-600">
            <X size={15} />
          </button>
        )}
        {searching && <Loader2 size={14} className="animate-spin text-brand" />}
      </div>

      {searchResults && (
        <div className="px-5 py-1.5 bg-slate-50 border-b border-slate-100 text-xs text-slate-500">
          {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
        </div>
      )}

      {/* Segments */}
      {display.length === 0 ? (
        <div className="p-8 text-center text-slate-500">
          {segments.length === 0
            ? 'No transcript available yet. Click "Refresh" after the recording has been processed by Zoom.'
            : 'No matches found.'}
        </div>
      ) : (
        <div className="max-h-[500px] overflow-y-auto divide-y divide-slate-50">
          {display.map((seg) => (
            <div key={seg.segment_index} className="px-5 py-2.5 hover:bg-slate-50 transition-colors">
              <div className="flex items-start gap-3">
                <span className="text-xs text-slate-400 font-mono mt-0.5 shrink-0 w-16 tabular-nums">
                  {fmtMs(seg.timestamp_start_ms)}
                </span>
                <div className="flex-1 min-w-0">
                  {seg.speaker && (
                    <span className="text-xs font-semibold text-slate-600 mr-1.5">{seg.speaker}:</span>
                  )}
                  <span className="text-sm text-slate-700">{seg.text}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Door check-ins tab ──────────────────────────────────
// Scans from the public conference check-in page, and what reconciliation
// made of them. Email matches are credited automatically once the last window
// closes; name matches and unmatched scans wait here for an admin.
const CAPTURE_STATUS = {
  name_match: { label: 'Name match', cls: 'bg-amber-100 text-amber-800', order: 0 },
  unmatched:  { label: 'Unmatched',  cls: 'bg-rose-100 text-rose-700',   order: 1 },
  pending:    { label: 'Waiting',    cls: 'bg-slate-100 text-slate-600', order: 2 },
  credited:   { label: 'Credited',   cls: 'bg-emerald-100 text-emerald-700', order: 3 },
  dismissed:  { label: 'Dismissed',  cls: 'bg-slate-100 text-slate-400', order: 4 },
};

function fmtScanTime(iso) {
  return new Date(iso).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });
}

function DoorCheckinsTab({ courseId, students, windows, onCredited }) {
  const [captures, setCaptures] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [picks, setPicks] = useState({});
  const [error, setError] = useState(null);

  const load = () =>
    api(`/api/admin/courses/${courseId}/checkin-captures`)
      .then((d) => setCaptures(d.captures || []))
      .catch(() => setError('Could not load door check-ins.'));
  useEffect(() => { load(); }, [courseId]);

  const reconcile = async () => {
    if (!confirm('Match door scans to the registration list now? Email matches are credited immediately.')) return;
    setReconciling(true); setError(null);
    try {
      const r = await api(`/api/admin/courses/${courseId}/reconcile-checkins`, { method: 'POST' });
      alert(`${r.credited} credited · ${r.name_match} name match${r.name_match === 1 ? '' : 'es'} to review · ${r.unmatched} unmatched`);
      await load(); onCredited();
    } catch { setError('Reconciliation failed.'); }
    finally { setReconciling(false); }
  };

  const resolve = async (c, body) => {
    setBusyId(c.id); setError(null);
    try {
      await api(`/api/admin/checkin-captures/${c.id}/resolve`, { method: 'POST', body });
      await load();
      if (body.enrollment_id) onCredited();
    } catch { setError('Could not update that scan.'); }
    finally { setBusyId(null); }
  };

  if (!captures) {
    return <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-500">{error || 'Loading…'}</div>;
  }

  const counts = Object.fromEntries(Object.keys(CAPTURE_STATUS).map((k) => [k, captures.filter((c) => c.status === k).length]));
  const sorted = [...captures].sort((a, b) =>
    CAPTURE_STATUS[a.status].order - CAPTURE_STATUS[b.status].order || new Date(a.captured_at) - new Date(b.captured_at));
  const registrants = [...students].sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));

  // In-person registrants without a credited scan for every required window —
  // the other half of reconciling against the registration list.
  const required = windows.filter((w) => w.required);
  const creditedBy = {};
  for (const c of captures) {
    if (c.status === 'credited') (creditedBy[c.enrollment_id] ||= new Set()).add(c.window_label);
  }
  const missing = students
    .filter((s) => s.attendance_mode === 'in_person')
    .map((s) => ({ s, got: creditedBy[s.enrollment_id] || new Set() }))
    .filter(({ got }) => required.some((w) => !got.has(w.label)));
  const anyReconciled = captures.some((c) => c.status !== 'pending');

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-lg border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-semibold">Door check-ins</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Scans from the conference check-in page. They're matched to registrations automatically once the
              last window closes; email matches are credited, the rest wait for you here.
            </p>
          </div>
          <button
            onClick={reconcile}
            disabled={reconciling || !captures.length}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-700 disabled:opacity-40"
          >
            {reconciling ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Reconcile now
          </button>
        </div>
        <div className="px-5 py-3 flex flex-wrap gap-2 text-xs border-b border-slate-100">
          {Object.entries(CAPTURE_STATUS).map(([k, v]) => (
            <span key={k} className={`px-2 py-0.5 rounded font-semibold ${v.cls}`}>{counts[k]} {v.label.toLowerCase()}</span>
          ))}
        </div>
        {error && <div className="px-5 py-2 text-sm text-rose-600">{error}</div>}

        {!captures.length ? (
          <div className="p-8 text-center text-slate-500 text-sm">No one has scanned in yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-5 py-2 font-semibold">Name given</th>
                  <th className="px-3 py-2 font-semibold">Email given</th>
                  <th className="px-3 py-2 font-semibold">Scan</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-5 py-2 font-semibold">Registration</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sorted.map((c) => {
                  const st = CAPTURE_STATUS[c.status];
                  const open = c.status === 'name_match' || c.status === 'unmatched' || c.status === 'pending';
                  return (
                    <tr key={c.id} className={c.status === 'dismissed' ? 'text-slate-400' : ''}>
                      <td className="px-5 py-2.5 font-medium">{c.full_name}</td>
                      <td className="px-3 py-2.5 text-slate-600">{c.email}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-xs">{c.window_label} · {fmtScanTime(c.captured_at)}</td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${st.cls}`}>{st.label}</span>
                        {c.status === 'pending' && !c.registered_at_scan && (
                          <div className="text-[11px] text-amber-700 mt-0.5">not on list at scan</div>
                        )}
                      </td>
                      <td className="px-5 py-2.5">
                        {c.status === 'credited' && <span className="text-slate-700">{c.enrollee_name} <span className="text-slate-400">({c.enrollee_email})</span></span>}
                        {c.status === 'name_match' && (
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-slate-700">{c.enrollee_name} <span className="text-slate-400">({c.enrollee_email})</span></span>
                            <button disabled={busyId === c.id} onClick={() => resolve(c, { enrollment_id: c.enrollment_id })}
                              className="text-xs font-semibold px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">Confirm</button>
                          </div>
                        )}
                        {(c.status === 'unmatched' || c.status === 'pending') && (
                          <div className="flex items-center gap-2 flex-wrap">
                            <select
                              value={picks[c.id] || ''}
                              onChange={(e) => setPicks((p) => ({ ...p, [c.id]: e.target.value }))}
                              className="text-xs border border-slate-200 rounded-md px-1.5 py-1 bg-white max-w-[14rem]"
                            >
                              <option value="">Link to registrant…</option>
                              {registrants.map((s) => (
                                <option key={s.enrollment_id} value={s.enrollment_id}>{s.full_name} ({s.email})</option>
                              ))}
                            </select>
                            <button disabled={!picks[c.id] || busyId === c.id} onClick={() => resolve(c, { enrollment_id: picks[c.id] })}
                              className="text-xs font-semibold px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40">Credit</button>
                          </div>
                        )}
                        {open && (
                          <button disabled={busyId === c.id} onClick={() => resolve(c, { dismiss: true })}
                            className="text-xs text-slate-500 hover:text-rose-700 mt-1">Dismiss</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {anyReconciled && missing.length > 0 && (
        <div className="bg-white rounded-lg border border-amber-200">
          <div className="px-5 py-3 border-b border-amber-100 flex items-center gap-2 text-amber-800">
            <AlertTriangle size={16} />
            <h3 className="font-semibold text-sm">
              {missing.length} in-person registrant{missing.length === 1 ? '' : 's'} without every scan credited
            </h3>
          </div>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-slate-100">
              {missing.map(({ s, got }) => (
                <tr key={s.enrollment_id}>
                  <td className="px-5 py-2 font-medium">{s.full_name}</td>
                  <td className="px-3 py-2 text-slate-600">{s.email}</td>
                  <td className="px-5 py-2 text-xs">
                    {required.map((w) => (
                      <span key={w.id} className={`inline-block mr-1.5 px-2 py-0.5 rounded font-semibold ${got.has(w.label) ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {got.has(w.label) ? '✓' : '✗'} {w.label}
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
